import { useState } from 'react';
import {
  useQualityRules,
  useQualityReports,
  useDeleteQualityRule,
} from '@/hooks/useQuality';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';
import { SkeletonTable } from '@/components/skeletons/SkeletonLoaders';
import { EmptyQualityRulesState, ErrorState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { QualityRuleModal } from '@/components/modals/QualityRuleModal';
import { useToast } from '@/hooks/useToast';
import { CheckCircle2, XCircle, AlertTriangle, Shield } from 'lucide-react';
import type { QualityRule } from '@/types';

export const Quality = () => {
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<QualityRule | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);

  const { data: rulesData, isLoading: rulesLoading, error: rulesError, refetch: refetchRules } = useQualityRules();
  const { data: reportsData, isLoading: reportsLoading, error: reportsError, refetch: refetchReports } = useQualityReports({ limit: 10 });

  const deleteRule = useDeleteQualityRule();
  const { toast } = useToast();

  const rules = rulesData?.data || [];
  const reports = reportsData?.items || [];

  const handleCreateRule = () => {
    setSelectedRule(null);
    setRuleModalOpen(true);
  };

  const handleEditRule = (rule: QualityRule) => {
    setSelectedRule(rule);
    setRuleModalOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setRuleToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ruleToDelete) return;

    try {
      await deleteRule.mutateAsync(ruleToDelete);
      toast({
        title: 'Success',
        description: 'Quality rule deleted successfully',
      });
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete quality rule',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Data Quality</h1>
          <p className="text-muted-foreground">
            Monitor and manage data quality checks
          </p>
        </div>
        <Button onClick={handleCreateRule}>
          Create Rule
        </Button>
      </div>

      {/* Quality Rules */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Quality Rules
          </CardTitle>
          <CardDescription>
            Define and manage data quality validation rules
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rulesLoading ? (
            <SkeletonTable rows={5} />
          ) : rulesError ? (
            <ErrorState
              message={rulesError instanceof Error ? rulesError.message : 'Failed to load quality rules'}
              onRetry={() => refetchRules()}
            />
          ) : rules.length === 0 ? (
            <EmptyQualityRulesState onCreate={handleCreateRule} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium">Name</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Type</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Description</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Status</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Created</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rules.map((rule) => (
                    <tr key={rule.id} className="hover:bg-muted/50">
                      <td className="px-4 py-2 font-medium">{rule.name}</td>
                      <td className="px-4 py-2">
                        <Badge variant="outline">{rule.type}</Badge>
                      </td>
                      <td className="px-4 py-2 text-sm text-muted-foreground max-w-xs truncate">
                        {rule.description || 'No description'}
                      </td>
                      <td className="px-4 py-2">
                        <Badge
                          variant={rule.enabled ? 'default' : 'secondary'}
                          className={
                            rule.enabled
                              ? 'bg-success/10 text-success hover:bg-success/10'
                              : ''
                          }
                        >
                          {rule.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {formatDate(rule.created_at)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditRule(rule)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteClick(rule.id)}
                            disabled={deleteRule.isPending}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quality History */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Quality Checks</CardTitle>
          <CardDescription>
            History of quality check executions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {reportsLoading ? (
            <SkeletonTable rows={5} />
          ) : reportsError ? (
            <ErrorState
              message={reportsError instanceof Error ? reportsError.message : 'Failed to load quality reports'}
              onRetry={() => refetchReports()}
            />
          ) : reports.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">
              No quality checks have been run yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium">Dataset</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Status</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Score</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Checks</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {reports.map((report) => (
                    <tr key={report.id} className="hover:bg-muted/50">
                      <td className="px-4 py-2 font-medium">
                        {report.dataset_name || report.dataset_id}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          {report.status === 'passed' ? (
                            <CheckCircle2 className="h-4 w-4 text-success" />
                          ) : report.status === 'failed' ? (
                            <XCircle className="h-4 w-4 text-destructive" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-warning" />
                          )}
                          <Badge
                            variant={
                              report.status === 'passed'
                                ? 'default'
                                : report.status === 'failed'
                                ? 'destructive'
                                : 'secondary'
                            }
                            className={
                              report.status === 'passed'
                                ? 'bg-success/10 text-success hover:bg-success/10'
                                : report.status === 'warning'
                                ? 'bg-warning/10 text-warning hover:bg-warning/10'
                                : ''
                            }
                          >
                            {report.status}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <span className="font-medium">{report.score}%</span>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <span className="text-success">{report.checks_passed}</span>
                        {' / '}
                        <span className="text-muted-foreground">{report.checks_run}</span>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {formatDate(report.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <QualityRuleModal
        open={ruleModalOpen}
        onOpenChange={setRuleModalOpen}
        rule={selectedRule}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Quality Rule"
        description="Are you sure you want to delete this quality rule? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        variant="destructive"
        loading={deleteRule.isPending}
      />
    </div>
  );
};
